import { useMutation, useQuery } from "@tanstack/react-query";
import { useGet, usePost, usePut } from "../../../../common/api-service";
import useAccessibilityModalStore from "../store";
import { BUModuleResponseModel, GAModuleResponseModel } from "./types";
import queryClient from "../../../../common/query-client";

//Bizbox Users Module
export const useGetAccess = (enabled: boolean) => {
  const { get } = useGet();
  const { userData } = useAccessibilityModalStore();
  const { setModule, setIsDefault, setIsGlobal } = useAccessibilityModalStore();
  var url = "/api/manage-bizbox-user-access?";

  if (userData?.userId) {
    url = `${url}userId=${encodeURIComponent(userData.userId)}`;
  }

  return useQuery<BUModuleResponseModel>({
    queryFn: async () => {
      const response = await get<BUModuleResponseModel>(url);
      setModule(response.modules);
      setIsDefault(response.isDefault);
      setIsGlobal(response.isGlobal);
      return response;
    },
    queryKey: [`bu-modules:${userData?.userId}`],
    enabled: enabled && userData?.userId !== undefined,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 0,
  });
};

export const useUpdateAccessibility = () => {
  const { put } = usePut();
  const state = useAccessibilityModalStore();

  return useMutation({
    mutationFn: () => {
      const payload = {
        UserId: state.userData?.userId,
        Modules: state.modules.map((module) => ({
          Id: module.id,
          Enable: module.enable,
          Privileges:
            module.privileges?.map((priv) => ({
              Id: priv.id,
              Enable: priv.enable,
            })) || [],
        })),
      };

      return put(`/api/manage-bizbox-user-access`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`bu-modules:${state.userData?.userId}`],
      });
    },
  });
};

export const useCreateAccessibility = () => {
  const { post } = usePost();
  const state = useAccessibilityModalStore();

  return useMutation({
    mutationFn: () => {
      const payload = {
        UserId: state.userData?.userId,
        Modules: state.modules.map((module) => ({
          Id: module.id,
          Enable: module.enable,
          Privileges:
            module.privileges?.map((priv) => ({
              Id: priv.id,
              Enable: priv.enable,
            })) || [],
        })),
      };

      return post(`/api/manage-bizbox-user-access`, payload, true, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`bu-modules:${state.userData?.userId}`],
      });
    },
  });
};

//Group Admins Module
export const useGetGAAccess = (enabled: boolean) => {
  const { get } = useGet();
  const { userData } = useAccessibilityModalStore();
  const { setModule, setIsDefault, setIsGlobal } = useAccessibilityModalStore();
  var url = "/api/manage-group-admin-access?";

  if (userData?.userId) {
    url = `${url}userId=${encodeURIComponent(userData.userId)}`;
  }

  return useQuery<GAModuleResponseModel>({
    queryFn: async () => {
      const response = await get<GAModuleResponseModel>(url);
      setModule(response.modules);
      setIsDefault(response.isDefault);
      setIsGlobal(response.isGlobal);
      return response;
    },
    queryKey: [`ga-modules:${userData?.userId}`],
    enabled: enabled && userData?.userId !== undefined,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 0,
  });
};

export const useUpdateGAAccessibility = () => {
  const { put } = usePut();
  const state = useAccessibilityModalStore();

  return useMutation({
    mutationFn: () => {
      const payload = {
        UserId: state.userData?.userId,
        Modules: state.modules.map((module) => ({
          Id: module.id,
          Enable: module.enable,
          Privileges:
            module.privileges?.map((priv) => ({
              Id: priv.id,
              Enable: priv.enable,
            })) || [],
        })),
      };

      return put(`/api/manage-group-admin-access`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`ga-modules:${state.userData?.userId}`],
      });
    },
  });
};

export const useCreateGAAccessibility = () => {
  const { post } = usePost();
  const state = useAccessibilityModalStore();

  return useMutation({
    mutationFn: () => {
      const payload = {
        UserId: state.userData?.userId,
        Modules: state.modules.map((module) => ({
          Id: module.id,
          Enable: module.enable,
          Privileges:
            module.privileges?.map((priv) => ({
              Id: priv.id,
              Enable: priv.enable,
            })) || [],
        })),
      };

      return post(`/api/manage-group-admin-access`, payload, true, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`ga-modules:${state.userData?.userId}`],
      });
    },
  });
};
